import { Lock } from "lucide-react"
import { cn } from "@/lib/utils"
import { useApp } from "@/hooks/useApp.jsx"
import { paywallTrigger } from "@/system/paywallTrigger.js"

export interface UpgradeOverlayProps {
  message?: string
  className?: string
}

/**
 * Blurred overlay shown over completed results for free-plan users. Renders
 * nothing for paid plans, so callers only need to decide whether real results
 * exist — the plan check lives here. The overlay fills its (relative) parent,
 * blurs the content beneath it, and blocks all interaction while presenting a
 * single Upgrade CTA that opens the same plans popup as the sidebar.
 */
export function UpgradeOverlay({
  message = "Upgrade to see your results",
  className,
}: UpgradeOverlayProps) {
  // The context is null outside AppProvider (and in the test harness), so this
  // must not destructure blind — matches the guard in BotDetail.
  const { session } = useApp() ?? {}
  const isFreePlan = session?.subscription?.slug === "free"
  if (!isFreePlan) return null

  return (
    <div
      className={cn(
        "absolute inset-0 z-20 flex items-center justify-center p-4 bg-background/40 backdrop-blur-sm",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-3 text-center max-w-xs">
        <span className="flex items-center justify-center h-10 w-10 rounded-full bg-muted text-muted-foreground">
          <Lock size={18} strokeWidth={1.5} />
        </span>
        <p className="text-body text-foreground">{message}</p>
        <button
          type="button"
          onClick={() => paywallTrigger.open()}
          className="inline-flex items-center h-8 px-4 rounded-md text-zinc-950 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 border border-amber-300/60 text-label font-medium cursor-pointer transition-colors"
        >
          Upgrade
        </button>
      </div>
    </div>
  )
}
